import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Save, ArrowLeft, Loader2, AlertCircle, Upload,
  X, Image as ImageIcon, Plus, Trash2,
} from 'lucide-react';
import { supabase } from '@/utils/supabase';

const EMPTY_PRODUCT = {
  name: '',
  slug: '',
  description: '',
  price: '',
  compare_price: '',
  cost: '',
  sku: '',
  barcode: '',
  inventory: '',
  weight: '',
  vendor: '',
  tags: '',
  status: 'active',
  featured: false,
  image_url: '',
  seo_title: '',
  seo_description: '',
};

function slugify(s) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function FormSection({ title, children }) {
  return (
    <div className="bg-[var(--paper)] border border-[var(--line)] p-5 space-y-4">
      {title && (
        <h2 className="font-['Space_Mono'] text-[0.62rem] tracking-[0.16em] uppercase text-[var(--muted)] border-b border-[var(--line)] pb-3">
          {title}
        </h2>
      )}
      {children}
    </div>
  );
}

function FieldGroup({ label, children, hint }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="font-['Space_Mono'] text-[0.6rem] tracking-[0.14em] uppercase text-[var(--muted)]">
        {label}
      </label>
      {children}
      {hint && <p className="text-[0.6rem] font-['Space_Mono'] text-[var(--muted)] mt-0.5">{hint}</p>}
    </div>
  );
}

function TextInput({ value, onChange, ...rest }) {
  return (
    <input
      value={value}
      onChange={onChange}
      className="field-surface px-3 py-2.5 text-sm w-full focus:outline-none focus:border-[var(--brand)] transition-colors"
      {...rest}
    />
  );
}

export default function AdminProductForm() {
  const navigate      = useNavigate();
  const { id }        = useParams();
  const isEdit        = Boolean(id);

  const [form, setForm]     = useState(EMPTY_PRODUCT);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const [success, setSuccess] = useState('');
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState('');

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      const { data, error: qErr } = await supabase.from('products').select('*').eq('id', id).single();
      if (qErr) { setError(qErr.message); setLoading(false); return; }
      setForm({
        name: data.name || '',
        slug: data.slug || slugify(data.name || ''),
        description: data.description || '',
        price: data.price ? (data.price / 100).toString() : '',
        compare_price: data.compare_price ? (data.compare_price / 100).toString() : '',
        cost: data.cost ? (data.cost / 100).toString() : '',
        sku: data.sku || '',
        barcode: data.barcode || '',
        inventory: data.inventory?.toString() || '',
        weight: data.weight?.toString() || '',
        vendor: data.vendor || '',
        tags: (data.tags || []).join(', '),
        status: data.status || 'active',
        featured: data.featured || false,
        image_url: data.image_url || '',
        seo_title: data.seo_title || '',
        seo_description: data.seo_description || '',
      });
      setImagePreview(data.image_url || '');
      setLoading(false);
    })();
  }, [id, isEdit]);

  const set = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      // Auto-generate slug from name
      if (field === 'name' && (!prev.slug || prev.slug === slugify(prev.name))) {
        next.slug = slugify(value);
      }
      return next;
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate type and size
    if (!file.type.startsWith('image/')) { setError('Please select an image file.'); return; }
    if (file.size > 5 * 1024 * 1024) { setError('Image must be under 5 MB.'); return; }

    setUploading(true);
    setError('');

    try {
      const ext = file.name.split('.').pop();
      const path = `products/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

      const { error: upErr } = await supabase.storage
        .from('product-images')
        .upload(path, file, { cacheControl: '3600', upsert: false });

      if (upErr) throw upErr;

      const { data: { publicUrl } } = supabase.storage
        .from('product-images')
        .getPublicUrl(path);

      setForm((prev) => ({ ...prev, image_url: publicUrl }));
      setImagePreview(publicUrl);
    } catch (err) {
      setError(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!form.name.trim()) { setError('Product name is required.'); return; }
    if (!form.price || isNaN(parseFloat(form.price))) { setError('Valid price is required.'); return; }

    setSaving(true);
    try {
      const payload = {
        name:           form.name.trim(),
        slug:           form.slug || slugify(form.name),
        description:    form.description || null,
        price:          Math.round(parseFloat(form.price) * 100),
        compare_price:  form.compare_price ? Math.round(parseFloat(form.compare_price) * 100) : 0,
        cost:           form.cost ? Math.round(parseFloat(form.cost) * 100) : 0,
        sku:            form.sku || null,
        barcode:        form.barcode || null,
        inventory:      form.inventory !== '' ? parseInt(form.inventory, 10) : null,
        weight:         form.weight ? parseFloat(form.weight) : 0,
        vendor:         form.vendor || null,
        tags:           form.tags ? form.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
        status:         form.status,
        featured:       form.featured,
        image_url:      form.image_url || null,
        seo_title:      form.seo_title || null,
        seo_description: form.seo_description || null,
      };

      if (isEdit) {
        const { error: uErr } = await supabase.from('products').update(payload).eq('id', id);
        if (uErr) throw uErr;
        setSuccess('Product updated.');
      } else {
        const { error: iErr } = await supabase.from('products').insert(payload);
        if (iErr) throw iErr;
        setSuccess('Product created.');
        setTimeout(() => navigate('/admin/products'), 800);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 size={20} className="animate-spin text-[var(--brand)]" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="p-6 space-y-4 max-w-4xl">
        {/* Header */}
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => navigate('/admin/products')}
            className="text-[var(--muted)] hover:text-[var(--ink)] transition-colors"
          >
            <ArrowLeft size={16} />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl text-[var(--ink)]">{isEdit ? 'Edit Product' : 'New Product'}</h1>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={form.status}
              onChange={set('status')}
              className="field-surface px-3 py-2 text-xs focus:outline-none focus:border-[var(--brand)] transition-colors font-['Space_Mono']"
            >
              <option value="active">Active</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
            </select>
            <button
              type="submit"
              disabled={saving}
              className="btn-brand px-4 py-2.5 flex items-center gap-2 text-[0.65rem] disabled:opacity-60"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="flex items-center gap-2 text-red-400 text-xs font-['Space_Mono'] p-3 bg-red-400/10 border border-red-400/20">
            <AlertCircle size={12} /><span>{error}</span>
          </div>
        )}
        {success && (
          <div className="text-emerald-400 text-xs font-['Space_Mono'] p-3 bg-emerald-400/10 border border-emerald-400/20">
            {success}
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-4">
          {/* Left: main fields */}
          <div className="lg:col-span-2 space-y-4">
            <FormSection>
              <FieldGroup label="Title">
                <TextInput value={form.name} onChange={set('name')} placeholder="Product name" required />
              </FieldGroup>
              <FieldGroup label="Slug" hint="URL-friendly identifier">
                <TextInput value={form.slug} onChange={set('slug')} placeholder="product-slug" />
              </FieldGroup>
              <FieldGroup label="Description">
                <textarea
                  value={form.description}
                  onChange={set('description')}
                  rows={5}
                  placeholder="Product description…"
                  className="field-surface px-3 py-2.5 text-sm w-full resize-none focus:outline-none focus:border-[var(--brand)] transition-colors"
                />
              </FieldGroup>
            </FormSection>

            <FormSection title="Pricing">
              <div className="grid grid-cols-3 gap-3">
                <FieldGroup label="Price ($)">
                  <TextInput value={form.price} onChange={set('price')} type="number" step="0.01" min="0" placeholder="0.00" required />
                </FieldGroup>
                <FieldGroup label="Compare Price ($)">
                  <TextInput value={form.compare_price} onChange={set('compare_price')} type="number" step="0.01" min="0" placeholder="0.00" />
                </FieldGroup>
                <FieldGroup label="Cost per item ($)">
                  <TextInput value={form.cost} onChange={set('cost')} type="number" step="0.01" min="0" placeholder="0.00" />
                </FieldGroup>
              </div>
            </FormSection>

            <FormSection title="Inventory">
              <div className="grid grid-cols-2 gap-3">
                <FieldGroup label="SKU">
                  <TextInput value={form.sku} onChange={set('sku')} placeholder="PROD-001" />
                </FieldGroup>
                <FieldGroup label="Barcode">
                  <TextInput value={form.barcode} onChange={set('barcode')} placeholder="ISBN, UPC, etc." />
                </FieldGroup>
              </div>
              <FieldGroup label="Quantity">
                <TextInput value={form.inventory} onChange={set('inventory')} type="number" min="0" placeholder="0" />
              </FieldGroup>
            </FormSection>

            <FormSection title="Shipping">
              <FieldGroup label="Weight (kg)">
                <TextInput value={form.weight} onChange={set('weight')} type="number" step="0.01" min="0" placeholder="0.00" />
              </FieldGroup>
            </FormSection>

            <FormSection title="SEO">
              <FieldGroup label="SEO Title">
                <TextInput value={form.seo_title} onChange={set('seo_title')} placeholder="Page title for search engines" />
              </FieldGroup>
              <FieldGroup label="SEO Description">
                <textarea
                  value={form.seo_description}
                  onChange={set('seo_description')}
                  rows={3}
                  placeholder="Meta description…"
                  className="field-surface px-3 py-2.5 text-sm w-full resize-none focus:outline-none focus:border-[var(--brand)] transition-colors"
                />
              </FieldGroup>
            </FormSection>
          </div>

          {/* Right: media + organization */}
          <div className="space-y-4">
            <FormSection title="Media">
              <div
                className="relative border-2 border-dashed border-[var(--line)] hover:border-[var(--brand)]/50 transition-colors"
                style={{ minHeight: '160px' }}
              >
                {imagePreview ? (
                  <div className="relative">
                    <img src={imagePreview} alt="Preview" className="w-full object-contain max-h-48 bg-[var(--bg)]" />
                    <button
                      type="button"
                      onClick={() => { setImagePreview(''); setForm((p) => ({ ...p, image_url: '' })); }}
                      className="absolute top-2 right-2 w-6 h-6 bg-black/70 text-white flex items-center justify-center hover:bg-red-500/80 transition-colors"
                    >
                      <X size={11} />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center h-40 cursor-pointer gap-2">
                    {uploading ? (
                      <Loader2 size={20} className="animate-spin text-[var(--brand)]" />
                    ) : (
                      <>
                        <ImageIcon size={20} className="text-[var(--muted)]" strokeWidth={1} />
                        <p className="text-xs font-['Space_Mono'] text-[var(--muted)]">Upload image</p>
                        <p className="text-[0.6rem] font-['Space_Mono'] text-[var(--muted)]">PNG, JPG, WebP · max 5 MB</p>
                      </>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      className="sr-only"
                      onChange={handleImageUpload}
                      disabled={uploading}
                    />
                  </label>
                )}
              </div>

              {/* Manual URL fallback */}
              <FieldGroup label="Or paste image URL">
                <TextInput
                  value={form.image_url}
                  onChange={(e) => { set('image_url')(e); setImagePreview(e.target.value); }}
                  placeholder="https://…"
                />
              </FieldGroup>
            </FormSection>

            <FormSection title="Organization">
              <FieldGroup label="Vendor">
                <TextInput value={form.vendor} onChange={set('vendor')} placeholder="Brand / Vendor name" />
              </FieldGroup>
              <FieldGroup label="Tags" hint="Comma-separated">
                <TextInput value={form.tags} onChange={set('tags')} placeholder="apparel, new, sale" />
              </FieldGroup>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="featured"
                  checked={form.featured}
                  onChange={set('featured')}
                  className="accent-[var(--brand)] w-4 h-4"
                />
                <label htmlFor="featured" className="text-sm text-[var(--ink)] font-['Space_Grotesk']">
                  Featured product
                </label>
              </div>
            </FormSection>
          </div>
        </div>
      </div>
    </form>
  );
}
